import {Injectable, Injector} from '@angular/core';
import {RestApiService} from './rest-api.service';
import {ConfigurationService} from '../../core/services/impl/configuration/configuration.service';
import {MetricsService, RequestMetricName} from './metrics.service';
import {RequestMetricResult} from '../components/models/metrics/request-metric-result.enum';
import {CustomMetricsName} from '../components/models/metrics/custom-metrics-name.enum';
import {MetricsType} from '../components/models/metrics/metrics-type.enum';
import {SessionService} from '../../core/services/impl/session/session.service';
import {configurationCode, env} from '../../core/utils/utils';
import {
  AztecScannedWebSocketHandlerService
} from '../../core/services/impl/websocket/aztec-scanned-web-socket-handler.service';
import {WebSocketService} from '../../core/services/impl/websocket/web-socket.service';
import {
  AuthenticateWebSocketHandlerService
} from '../../core/services/impl/websocket/authenticate-web-socket-handler.service';
import {SectionsStateService} from './sections-state.service';
import {ApplicationStateService} from './application-state.service';
import {
  VehicleModelStateService
} from '../../core/services/impl/section-services/vehicle-model/vehicle-model-state.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {BusinessParamName} from '../models/request-offer/request-offer.enum';
import {ObjectUtils} from '../../core/utils/object.utils';
import {UrlParamsService} from '../../core/services/impl/url-params.service';
import {CalculationService} from '../../core/services/impl/calculation.service';
import {RuntimeEnvironment} from '../enums/runtime-environment.enum';
import {ScrollHelper} from './scroll-helper';
import {DialogKey} from '../components/dialogs/dialogs-configuration';
import {DialogService} from '../../core/services/dialog.service';
import {
  AztecScannerQrDialogComponent
} from '../components/dialogs/aztec-scanner-qr-dialog/aztec-scanner-qr-dialog.component';
import {ContactDataModel} from '../../core/models/contact-data.model';
import {DeviceService} from './device.service';
import {ApiRequestKey} from '../enums/api-request-key.enum';
import {ApiParamsService} from '../../core/services/api-params.service';
import {QueryParamName} from '../../core/enums/query-param-name.enum';
import {Subscription} from 'rxjs/Rx';

@Injectable({
  providedIn: 'root'
})
export class AztecScannerService {

  scanningSuccess: boolean;
  private readonly metricName = {
    className: 'AztecScannerService',
    methodName: 'sendDecodedAztec'
  };
  private webSocketService: WebSocketService;
  private readonly API_REQUEST_KEY: ApiRequestKey = ApiRequestKey.DECODE_AZTEC;
  private subscriptions: Subscription[] = [];

  get barcodeEndpoint(): string {
    switch (env()) {
      case RuntimeEnvironment.DEV:
        return 'https://api-dev.kioskpolis.pl/oc-ac/utils/1.0.0/barcodes/generate';
      case RuntimeEnvironment.TEST:
        return 'https://api-test.kioskpolis.pl/oc-ac/utils/1.0.0/barcodes/generate';
      case RuntimeEnvironment.PROD:
        return 'https://api.kioskpolis.pl/oc-ac/utils/1.0.0/barcodes/generate';
      default:
        return 'https://api-test.kioskpolis.pl/oc-ac/utils/1.0.0/barcodes/generate';
    }
  }
  
  constructor(private apiService: RestApiService,
              private sessionService: SessionService,
              private sectionStateService: SectionsStateService,
              private stateService: ApplicationStateService,
              private vehicleModelStateService: VehicleModelStateService,
              private injector: Injector,
              private deviceService: DeviceService,
              private dialogService: DialogService,
              private urlParamsService: UrlParamsService,
              private calculationService: CalculationService,
              private metricsService: MetricsService,
              private configuration: ConfigurationService,
              private apiParamsService: ApiParamsService) {
  }

  public async getAztecScannerQrCode(): Promise<any> {
    const headers = {
      'Accept': 'application/json'
    };

    // create new session for aztec data to avoid overwriting existing session data
    if (this.stateService.state.calculationDataFromSession) {
      await this.sessionService.resetSession();
    }

    return this.apiService.get(this.barcodeEndpoint, {
      sessionId: this.sessionService.sessionId,
      configurationCode: configurationCode(),
      barcode: 'QR_AZTEC'
    }, true, headers)
        .then(resp => {
          return resp.data
        });
  }
  
  public sendDecodedAztec(aztecCode): Promise<any> {
    const headers = {
      'Accept': 'application/json'
    };

    const body = this.createPayload(aztecCode);
    this.metricsService.sendRequestMetric(<RequestMetricName>{
      ...this.metricName,
      type: RequestMetricResult.SEND
    }, body);

    return this.apiService.post(this.configuration.uiConfig.aztecHandlingEndpoint,
      body, false, headers)
      .then(resp => {
        this.sendResponseMetrics(resp);
        return resp;
      })
      .catch(err => {
        this.metricsService.sendRequestMetric({
          ...this.metricName,
          type: RequestMetricResult.ERROR
        }, err);
      });
  }
  
  public createPayload(aztecCode: string): any {
    return {
      header: {
        processContext: {
          businessParameters: this.apiParamsService.createBusinessParams({
            requestKey: this.API_REQUEST_KEY
          })
        }
      },
      body: {
        aztecCode: aztecCode
      }
    }
  }

  handleAztecScannedWebsocket() {
    this.webSocketService = this.injector.get(WebSocketService);
    let authenticateWebSocketHandlerService = this.injector.get(AuthenticateWebSocketHandlerService);
    const authenticationSubscription = authenticateWebSocketHandlerService.authObservable.subscribe(() => {
      let aztecScannedWebSocketHandlerService: AztecScannedWebSocketHandlerService = this.injector.get(AztecScannedWebSocketHandlerService);
      const aztecScannedSubscription = aztecScannedWebSocketHandlerService.aztecScannedObservable
          .subscribe(async data => {
            if (data.status && data.status.code === 'AZTEC_SCANNED' && data.status.statusCode === 200) {
              this.sessionService.cleanSessionData();
              this.stateService.state.aztecScannedFromQrCode = true;
              await this.getSessionData();
              this.stateService.updateApplicationState();
              this.closeAztecScannerQRDialog();
              this.resetSubscriptions();
            }
          });
      this.subscriptions.push(aztecScannedSubscription);
    });
    this.subscriptions.push(authenticationSubscription);
    this.webSocketService.connect();
  }

  resetAztecScannedWebsocket(): void {
    this.resetSubscriptions();
    this.webSocketService.disconnect();
    this.handleAztecScannedWebsocket();
  }

  scan(): void {
    if (this.deviceService.isMobileDevice) {
      this.openAztecScanner();
    } else {
      this.openAztecScannerQRDialog();
    }
  }

  openAztecScanner(): void {
    this.stateService.aztecScannerActive = true;
    this.updateBodyClass(this.stateService.aztecScannerActive);
    setTimeout(() => {
      (new ScrollHelper()).scrollIntoView('.vehicle-model-section__aztec-scanner', {behavior: 'smooth'});
    }, 10);
  }

  async openAztecScannerQRDialog() {
    const qrCodeData = await this.getAztecScannerQrCode();
    this.dialogService.open(DialogKey.AZTEC_SCANNER_QR_DIALOG, AztecScannerQrDialogComponent.getConfigBuilder().buildConfiguration({
      qrCodeData: qrCodeData
    }));
    this.handleAztecScannedWebsocket();
  }

  closeAztecScannerQRDialog(): void {
    this.dialogService.close(DialogKey.AZTEC_SCANNER_QR_DIALOG);
  }

  closeAztecScanner(scanningSuccess: boolean) {
    this.stateService.aztecScannerActive = false;
    this.updateBodyClass(this.stateService.aztecScannerActive);
    this.scanningSuccess = scanningSuccess;
  }

  private getSessionData() {
    return new Promise(res => {
      this.sessionService.getSessionDetails()
          .then(data => {
            this.sectionStateService.prepareFirstStepAfterAztecData();
            this.stateService.handleRegistrationDataFromAztec(data.values.body);
            this.handleCalculationData(data.values.body);
            this.vehicleModelStateService.init();
            res();
          });
    });
  }

  private handleCalculationData(data: any): void {
    if (data.hasOwnProperty('calculationData')) {
      this.stateService.state.dataFromAztec = true;
      this.stateService.state.usedPreviewMode = true;
      const calculationData = this.mergeWithExistingCalculationData(data.calculationData);

      if (!isNotNullOrUndefined(calculationData.contact)) {
        calculationData.contact = new ContactDataModel();
      }

      this.calculationService.dataFromSession.emit(calculationData);

      const sourceFromUrl = this.urlParamsService.getQueryParam(QueryParamName.SOURCE);
      if (!isNotNullOrUndefined(sourceFromUrl)) {
        this.stateService.saveBusinessParameter(BusinessParamName.CLIENT_SOURCE, 'aztec');
      }
    }
  }

  private mergeWithExistingCalculationData(calculationData: any): any {
    return ObjectUtils.mergeDeep(this.calculationService.calculationData, calculationData);
  }

  private sendResponseMetrics(resp: any): void {
    this.metricsService.sendRequestMetric({
      ...this.metricName,
      type: RequestMetricResult.SUCCESS
    });
    this.metricsService.sendSimpleMetric(CustomMetricsName.AZTEC_DATA, MetricsType.COLLECTED_DATA, JSON.stringify(resp));
  }

  private updateBodyClass(aztecScannerOpened: boolean): void {
    const body = document.querySelector('body');
    const aztecScannerActiveBodyClass = 'body--aztec-scanner-active';
    if (aztecScannerOpened) {
      body.classList.add(aztecScannerActiveBodyClass);
    } else {
      body.classList.remove(aztecScannerActiveBodyClass);
    }
  }

  private resetSubscriptions(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];
  }
}

